import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getMe, getQualityContext, mintLaborClaimToken, type MeResponse, type QualityContext } from '../lib/api';

/** Phase 2 · the labor master DB surfaced at comparison time. Per-bidder deterministic quality_score +
 *  the negative/positive flags + eligibility (blacklist/cert/status). Advisory context (the deterministic
 *  quality_adjustment is applied in the roll-up, default-OFF) — never the sole basis for a decision.
 *  GM also gets a "claim link" action per company (mints a single-use labor-portal claim token). */
export function CompanyHistoryPanel({ pid }: { pid: string }) {
  const { data } = useQuery<QualityContext>({ queryKey: ['quality-context', pid], queryFn: () => getQualityContext(pid) });
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  const isGm = me?.account?.role === 'gm';
  const [tokens, setTokens] = useState<Record<string, string>>({});
  const companies = data?.companies ?? [];
  if (companies.length === 0) return null;

  async function mint(lcId: string) {
    try { const r = await mintLaborClaimToken(lcId); setTokens((t) => ({ ...t, [lcId]: r.token })); }
    catch { /* surfaced inline below by absence of a token */ }
  }

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <h4 style={{ margin: '0 0 4px' }}>劳务方履历 / Company history &amp; quality</h4>
      <div className="muted small" style={{ marginBottom: 8 }}>来自劳务主数据（信誉碎片确定性汇总）。质量分 0–100；黑名单 / 资质过期 / 不满意终验在此显示。参考性上下文，非唯一裁决依据。</div>
      <div className="row-wrap" style={{ gap: 8 }}>
        {companies.map((c) => {
          const score = c.quality_score ?? 100;
          const pill = score >= 85 ? 'pill-good' : score >= 60 ? 'pill-warn' : 'pill-bad';
          const m = c.metrics ?? {};
          const num = (k: string) => Number(m[k]) || 0;
          return (
            <div key={c.labor_co_id} className="card" style={{ minWidth: 210, flex: 1, background: 'var(--paper)' }}>
              <div className="row-wrap" style={{ gap: 6, alignItems: 'center', marginBottom: 4 }}>
                <strong>{c.name ?? c.labor_co_id}</strong>
                <span className={`pill ${pill} small`}>质量分 {score}</span>
                {!c.eligibility.eligible && (
                  <span className="pill pill-bad small" title={c.eligibility.reason_zh}>
                    {c.eligibility.mode === 'gate' ? '不可用' : '受限'}：{c.eligibility.triggered_rules.join('、')}
                  </span>
                )}
              </div>
              {c.narrative_flags.length > 0 && (
                <div className="row-wrap small" style={{ gap: 4, marginBottom: 4 }}>
                  {c.narrative_flags.slice(0, 6).map((f) => <span key={f} className="pill pill-muted small">{f}</span>)}
                </div>
              )}
              <div className="muted small">
                {m.nine_x_triggered ? '⚠ 9X 非履约 · ' : ''}
                {num('sla_hard_breaches_total') > 0 ? `SLA硬违约 ${num('sla_hard_breaches_total')} · ` : ''}
                {num('final_inspection_unresolved_total') > 0 ? `终验未闭/不满意 ${num('final_inspection_unresolved_total')} · ` : ''}
                {num('payment_overclaim_flags_total') > 0 ? `超额申报 ${num('payment_overclaim_flags_total')} · ` : ''}
                {num('clean_final_inspection_count') > 0 ? `清洁终验 ${num('clean_final_inspection_count')}` : ''}
                {!m.nine_x_triggered && num('sla_hard_breaches_total') === 0 && num('final_inspection_unresolved_total') === 0 && num('payment_overclaim_flags_total') === 0 ? '暂无负面硬信号 / no negative signals' : ''}
              </div>
              {isGm && (
                <div style={{ marginTop: 6 }}>
                  {tokens[c.labor_co_id]
                    ? <input readOnly value={tokens[c.labor_co_id]} onFocus={(e) => e.currentTarget.select()} title="把此令牌发给该劳务公司，在「劳务公司登录」处认领" style={{ width: '100%', fontSize: 11 }} />
                    : <button className="small" onClick={() => mint(c.labor_co_id)}>生成认领链接 / Claim link</button>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
